<template>
  <div>
    <!-- Web-owned action bar (same pattern as cardtransactions) — native draws the
         app header above it; this strip carries the in-flow Back action. -->
    <div class="navless-layout-header">
      <slot name='navbar'>
        <ed-button :to='$routerHistory.previous().path'>
          <i class="fal fa-chevron-left"></i>
          &nbsp;
          Back
        </ed-button>
      </slot>
    </div>

    <div v-if="errorMessage && !isReady" class="submit-receipt__error">
      {{ errorMessage }}
    </div>

    <!-- While the scan is in flight the page body is intentionally empty. The
         mobile.vue "Uploading receipt…" overlay persists over the whole flow
         (covers upload + scan) and is dropped by SubmitReceipt's watcher when
         `isReady` flips true (or `errorMessage` is set on failure). Gating on
         `isReady` (not just `scan`) prevents an empty-candidates flash during
         the intermediate Uploaded/Scanning state. -->
    <div v-else-if="scan && isReady" class="submit-receipt">
      <!-- Image — shared ReceiptImageViewer (same component as the Reconcile
           preview) brings the +/− zoom pill and width-fit rendering instead of
           the old object-fit:cover crop that clipped wide receipts. -->
      <div class="submit-receipt__section">
        <h4 class="submit-receipt__section-label">Image</h4>
        <ReceiptImageViewer
          :image-url="imageUrl"
          :content-type="previewContentType"
          :errored="imageErrored"
          wrap-height="476px"
        />
      </div>

      <!-- Receipt Data — header lives inside the card per Figma 533:287 -->
      <div class="submit-receipt__data-card">
        <h4 class="submit-receipt__data-card-title">Receipt Data</h4>
        <p v-if="hasNoPrefill" class="submit-receipt__data-card-fallback">
          Scan didn't extract data — proceed to match or save for later.
        </p>
        <template v-else>
          <div class="submit-receipt__row">
            <div class="submit-receipt__field">
              <span class="submit-receipt__label">Merchant</span>
              <span class="submit-receipt__value">{{ merchant || '—' }}</span>
            </div>
            <div class="submit-receipt__field submit-receipt__field--right">
              <span class="submit-receipt__label">Amount</span>
              <span class="submit-receipt__value">{{ formattedAmount }}</span>
            </div>
          </div>
          <div class="submit-receipt__field">
            <span class="submit-receipt__label">Transaction Date</span>
            <span class="submit-receipt__value">{{ formattedDate }}</span>
          </div>
        </template>
      </div>

      <button
        type="button"
        class="submit-receipt__folder-cta"
        :disabled="busy"
        @click="onSaveForLater"
      >
        ADD TO RECEIPT FOLDER
      </button>

      <!-- Link to transaction -->
      <h4 class="submit-receipt__section-label">Link to transaction</h4>
      <div v-if="candidates.length" class="submit-receipt__candidates">
        <button
          v-for="candidate in candidates"
          :key="candidate.legacyTransactionId"
          type="button"
          class="submit-receipt__candidate"
          :class="{ 'is-selected': isSelected(candidate) }"
          :data-status="statusColor(candidate)"
          @click="selectedCandidate = candidate"
        >
          <span class="submit-receipt__candidate-stripe" :data-status="statusColor(candidate)"></span>
          <span class="submit-receipt__candidate-body">
            <span class="submit-receipt__candidate-top">
              <span class="submit-receipt__candidate-merchant">{{ candidate.merchantName || '—' }}</span>
              <span class="submit-receipt__candidate-amount">{{ formatCurrency(candidate.amount, candidate.currencyCode) }}</span>
            </span>
            <span class="submit-receipt__candidate-date">{{ formatDate(candidate.transactionDate) }}</span>
          </span>
          <span class="submit-receipt__candidate-rail">
            <span class="submit-receipt__candidate-dot" :class="{ 'is-selected': isSelected(candidate) }"></span>
          </span>
        </button>
      </div>
      <div v-else class="submit-receipt__empty">
        No open or pending transactions to match. Save the receipt to your folder for later.
      </div>

      <button
        type="button"
        class="submit-receipt__submit"
        :disabled="!selectedCandidate || busy"
        @click="onSubmit"
      >
        {{ busy ? 'Working…' : 'Submit' }}
      </button>

      <div v-if="errorMessage" class="submit-receipt__error">
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<script>
import receiptScansApi from '@/core/api/receiptScans'
import createReceiptScanPoller from '@/core/receiptScan/receiptScanPoller'
import ReceiptImageViewer from '@/components/_modules/Programs/Card-transactions/ReceiptImageViewer.vue'
import { EventBus } from '@/event-bus'

const READY_STATUSES = ['ReadyForMatch', 'Matched', 'Submitted', 'FolderSaved']

export default {
  alias: 'evedpaysubmitreceipt',

  components: {
    ReceiptImageViewer
  },

  data() {
    return {
      receiptScanId: this.$route.query.receiptScanId || null,
      scan: null,
      imageUrl: null,
      imageContentType: null,
      imageErrored: false,
      busy: false,
      errorMessage: null,
      selectedCandidate: null,
      merchant: '',
      amount: null,
      transactionDate: null,
      category: '',
      poller: null
    }
  },

  computed: {
    moment() { return global.moment },
    candidates() {
      return (this.scan && this.scan.matchCandidates) || []
    },
    formattedAmount() {
      if (this.amount == null || isNaN(this.amount)) return '—'
      const n = Number(this.amount)
      const sign = n < 0 ? '-' : ''
      return sign + '$' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formattedDate() {
      if (!this.transactionDate) return '—'
      return this.moment(this.transactionDate).format('MMM DD YYYY')
    },
    isReady() {
      return !!(this.scan && READY_STATUSES.indexOf(this.scan.status) >= 0)
    },
    hasNoPrefill() {
      return !this.merchant && this.amount == null && !this.transactionDate
    },
    previewContentType() {
      // getImage's contentType wins; fall back to the scan DTO's FileInfo. Both
      // carry the stored upload content-type — needed so PDF receipts (library
      // picker accepts application/pdf) hit the viewer's iframe branch instead
      // of rendering as a broken <img>.
      return this.imageContentType ||
        (this.scan && this.scan.fileInfo && this.scan.fileInfo.contentType) || null
    }
  },

  created() {
    if (this.receiptScanId) {
      this.fetchScan()
    } else {
      this.errorMessage = 'No receipt selected. Capture or pick a receipt first.'
    }
  },

  watch: {
    // Tell mobile.vue to drop the "Uploading receipt…" overlay the moment we
    // actually have something to show — either the candidates (isReady) or an
    // error message. Until then the overlay covers the empty SubmitReceipt body
    // so the user never sees a blank page during the 10–20s Photon scan.
    isReady(ready) {
      if (ready) {
        EventBus.$emit('submit-receipt-ready')
      }
    },
    errorMessage(msg) {
      if (msg) {
        EventBus.$emit('submit-receipt-ready')
      }
    }
  },

  beforeDestroy() {
    if (this.poller) {
      this.poller.cancel()
      this.poller = null
    }
  },

  methods: {
    // Initial candidate selection: pre-select the backend-suggested candidate, or NOTHING when
    // none cleared the confidence floor (PRODUCTDEV-5475). Replaces the old `suggested || cs[0]`
    // fallback, which confidently pre-selected the top noise candidate on a no-match receipt.
    pickInitialCandidate(candidates) {
      return (candidates || []).find(c => c.isSuggested) || null
    },

    async fetchScan() {
      try {
        const r = await receiptScansApi.getStatus(this.receiptScanId)
        this.scan = r
        this.applyPrefill(r)
        this.loadImage()
        if (!this.isReady) {
          // Scan still in flight — start polling and let the progress UI render.
          // candidates / selection are populated by the poller's onDone callback.
          this.startPolling()
          return
        }
        const cs = (r && r.matchCandidates) || []
        this.selectedCandidate = this.pickInitialCandidate(cs)
      } catch (err) {
        this.errorMessage = 'Could not load receipt. Please try again.'
      }
    },

    startPolling() {
      if (this.poller) return
      var self = this
      this.poller = createReceiptScanPoller({
        receiptScanId: this.receiptScanId,
        contextHint: { source: 'submitReceipt' },
        onDone: function (result) {
          self.poller = null
          // Use the poller's final getStatus payload directly — `result.data` is the
          // full response from the last poll cycle. The previous implementation made
          // a second getStatus call here, which left the 3-step progress card on
          // screen for an extra ~1s while the (already-known) ready data round-tripped
          // back from Platform. That was the "second loading screen" the user saw.
          var scanData = result && result.data
          if (!scanData) {
            self.errorMessage = 'Could not load receipt. Please try again.'
            return
          }
          self.scan = scanData
          self.applyPrefill(scanData)
          self.loadImage()
          var cs = scanData.matchCandidates || []
          self.selectedCandidate = self.pickInitialCandidate(cs)
        },
        onError: function (error) {
          self.poller = null
          if (error.type === 'scan_failed') {
            self.errorMessage = 'Scan failed. Please try uploading the receipt again.'
          } else if (error.type === 'timeout' || error.type === 'unreachable') {
            self.errorMessage = 'Scan is taking longer than expected. Save the receipt to your folder and try matching later.'
          } else {
            self.errorMessage = 'Error scanning receipt. Please try again.'
          }
        }
      })
      this.poller.start()
    },

    applyPrefill(data) {
      const autoFill = (data && data.autoFill) || (data && data.autoFillJson) || {}
      this.merchant = autoFill.MerchantName || autoFill.merchantName || (data && data.merchant) || ''
      const amountObj = autoFill.amount || autoFill.Amount
      if (amountObj && amountObj.total != null) {
        this.amount = amountObj.total
      } else if (autoFill.PhotonTotal != null) {
        this.amount = autoFill.PhotonTotal
      } else if (autoFill.photonTotal != null) {
        this.amount = autoFill.photonTotal
      } else {
        this.amount = (data && data.amount) != null ? data.amount : null
      }
      this.transactionDate = autoFill.TransactionDate || autoFill.transactionDate || (data && data.transactionDate) || null
      this.category = autoFill.Category || autoFill.category || ''
    },
    async loadImage() {
      try {
        const r = await receiptScansApi.getImage(this.receiptScanId)
        if (r && typeof r === 'object') {
          this.imageUrl = r.presignedUrl || r.url || null
          this.imageContentType = r.contentType || null
        } else if (typeof r === 'string') {
          this.imageUrl = r
        }
        // No URL and nothing previously loaded → show the viewer's "Image
        // unavailable" fallback instead of an endless spinner. A failed refresh
        // keeps an already-loaded image on screen.
        this.imageErrored = !this.imageUrl
      } catch (err) {
        this.imageErrored = !this.imageUrl
      }
    },
    isSelected(candidate) {
      return this.selectedCandidate &&
        this.selectedCandidate.legacyTransactionId === candidate.legacyTransactionId
    },
    // Status colour for the left stripe. The ranker only returns receiptable (Open/Pending)
    // candidates and MatchCandidateDto carries no per-candidate status field, so the stripe is
    // always yellow. The previous green/red branches keyed off candidate.transactionStatus,
    // which the API never serves — dead code. If MatchCandidateDto ever exposes TransactionStatus
    // (the data exists on EvedPayCardTransactionReadModel; Platform PR #35502), re-introduce the
    // green (Reconciled/Matched) / red (Denied/Rejected) mapping here.
    statusColor() {
      return 'yellow'
    },
    formatCurrency(amount, currencyCode) {
      if (typeof amount !== 'number') return '$0.00'
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode || 'USD'
      }).format(amount)
    },
    formatDate(dateStr) {
      if (!dateStr) return 'N/A'
      const d = new Date(dateStr)
      if (isNaN(d.getTime())) return dateStr
      return global.moment(d).format('MMM DD YYYY')
    },
    async onSubmit() {
      if (!this.selectedCandidate || !this.scan) return
      this.busy = true
      this.errorMessage = null
      try {
        await receiptScansApi.matchToTransaction(
          this.receiptScanId,
          this.selectedCandidate.legacyTransactionId,
          this.scan.rowVersion
        )
        // Pass receiptScanId alongside transactionid so ReconcileTransaction.tryApplyReceiptScanPrefill()
        // can read it from $route.query and fill the form from the Photon extraction (Amount, line items,
        // mismatch detection). Without this query param, the prefill mixin no-ops and the user sees an
        // empty form despite the OCR having extracted the data.
        this.$router.push({
          path: '/evedpay/transactiondetails',
          query: {
            transactionid: this.selectedCandidate.legacyTransactionId,
            receiptScanId: this.receiptScanId
          }
        })
      } catch (err) {
        this.errorMessage = 'Failed to match receipt. Please try again.'
      } finally {
        this.busy = false
      }
    },
    async onSaveForLater() {
      if (!this.scan) return
      this.busy = true
      this.errorMessage = null
      try {
        await receiptScansApi.saveToFolder(this.receiptScanId, (this.scan && this.scan.rowVersion) || '')
        // Spec (Ticket 5): "the user can be returned to the page they started from
        // … and a small message appears at the bottom of the app confirming the action".
        // Return to /evedpay/cards (FAB origin) and surface the confirmation toast.
        this.$notifications.notify({
          type: 'info',
          message: 'Image Added to Receipt Folder',
          placement: { from: 'bottom', align: 'center' }
        })
        this.$router.push({ path: '/evedpay/cards' })
      } catch (err) {
        this.errorMessage = 'Could not save receipt. Please try again.'
      } finally {
        this.busy = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
/* Same row layout as the cardtransactions Back bar (purple bg/colors come from
 * the mobile layout's global .navless-layout-header theme styles). */
.navless-layout-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* Page body — Figma 533:287: 8px side gutters, Source Sans Pro throughout
 * (same family the transaction chips/list use). */
.submit-receipt {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 8px 8px 16px;
  font-family: 'Source Sans Pro', sans-serif;
}

.submit-receipt__section {
  margin-bottom: 8px;
}

.submit-receipt__section-label {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px;
}

/* Image surface — same treatment as the Reconcile preview panel (Figma
 * 1494:4169): #fafafa under the photo, thin #ececec border, square corners.
 * `>>>` pierces ReceiptImageViewer's scoped block. */
.submit-receipt__section >>> .receipt-image-wrap {
  background-color: #fafafa;
  border: 1px solid #ececec;
  border-radius: 0;
}

/* Receipt Data card — Figma 657:1915: 8px padding, 6px row gap, 3px radius,
 * 0 2px 6px shadow. NO divider lines between fields. */
.submit-receipt__data-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: #ffffff;
  border-radius: 3px;
  padding: 8px;
  margin-bottom: 16px;
  box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.2);
}

.submit-receipt__data-card-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.submit-receipt__data-card-fallback {
  margin: 0;
  font-size: 13px;
  color: #6b6b6b;
  line-height: 1.4;
}

.submit-receipt__row {
  display: flex;
  justify-content: space-between;
  gap: 24px;

  .submit-receipt__field:first-child {
    flex: 1 1 auto;
    min-width: 0;
  }
}

.submit-receipt__field {
  display: flex;
  flex-direction: column;

  &--right {
    flex: 0 0 auto;
    align-items: flex-end;
    text-align: right;
  }
}

.submit-receipt__label {
  font-size: 14px;
  font-weight: 400;
  color: #333;
  line-height: 20px;
}

.submit-receipt__value {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  line-height: 20px;
}

/* Secondary button — Figma 657:1848: 32px tall, #f8f8f8 on a #999 border,
 * 3px radius, 14px semibold uppercase. */
.submit-receipt__folder-cta {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 32px;
  background: #f8f8f8;
  color: #333;
  border: 1px solid #999;
  border-radius: 3px;
  padding: 0 17px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  margin: 0 0 8px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #f0f0f0;
  }

  &:disabled {
    color: #bbb;
    border-color: #ddd;
    cursor: not-allowed;
  }
}

.submit-receipt__candidates {
  display: flex;
  flex-direction: column;
  gap: 8px;
  // Guarantee breathing space before the Submit button. The Submit button has
  // `margin-top: auto` to pin itself to the bottom of the flex column, which
  // collapses to 0 when the candidate list is long enough to fill the viewport —
  // without this margin-bottom the last candidate sits flush against Submit.
  margin-bottom: 1.25rem;
}

/* Candidate card — Figma 657:1938: 71px row, flush 4px status stripe on the
 * left, #fafafa radio rail on the right, 3px radius, 0 2px 6px shadow;
 * selected = 1px #0090ff border (transparent border otherwise so selection
 * doesn't shift layout). */
.submit-receipt__candidate {
  position: relative;
  display: flex;
  align-items: stretch;
  min-height: 71px;
  padding: 0;
  background: #ffffff;
  border: 1px solid transparent;
  border-radius: 3px;
  box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.2);
  cursor: pointer;
  overflow: hidden;
  text-align: left;
  width: 100%;
  font: inherit;
  color: inherit;
  transition: border-color 0.15s;

  &.is-selected {
    border-color: #0090ff;
  }
}

// Status stripe — full-height left-edge bar (card's overflow:hidden rounds it
// with the 3px corners). #ffb100 matches the transaction chips' stripe.
.submit-receipt__candidate-stripe {
  flex: 0 0 4px;

  &[data-status='yellow'] { background: #ffb100; }
  &[data-status='green']  { background: #4caf50; }
  &[data-status='red']    { background: #e53935; }
}

.submit-receipt__candidate-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  padding: 12px 14px 12px 8px;
}

.submit-receipt__candidate-top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
}

.submit-receipt__candidate-merchant {
  font-weight: 600;
  color: #333;
  font-size: 14px;
  line-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.submit-receipt__candidate-amount {
  font-weight: 600;
  color: #333;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
}

.submit-receipt__candidate-date {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.85);
}

// Radio rail — light full-height strip behind the selection dot, per Figma
// 657:1952 (rounded corners come from the card's overflow:hidden).
.submit-receipt__candidate-rail {
  flex: 0 0 33px;
  background: #fafafa;
  display: flex;
  align-items: center;
  justify-content: center;
}

.submit-receipt__candidate-dot {
  width: 20px;
  height: 20px;
  border: 2px solid #d0d0d0;
  border-radius: 50%;
  background: #ffffff;
  transition: background 0.15s, border-color 0.15s;

  &.is-selected {
    background: #0090ff;
    border-color: #0090ff;
    box-shadow: inset 0 0 0 3px #ffffff;
  }
}

.submit-receipt__empty {
  padding: 1rem;
  text-align: center;
  color: #888;
  font-size: 14px;
  background: #fafafa;
  border-radius: 6px;
  margin-bottom: 1.25rem;
}

// Submit pinned to the bottom of the page area — no empty scroll space below it.
.submit-receipt__submit {
  display: block;
  width: 100%;
  background: #1976d2;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 14px;
  font-size: 16px;
  font-weight: 600;
  margin: auto 0 0;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #1565c0;
  }

  &:disabled {
    background: #b0c8df;
    cursor: not-allowed;
  }
}

.submit-receipt__error {
  margin: 1rem 0 0;
  padding: 0.75rem;
  background: #fdecea;
  color: #c62828;
  border-radius: 6px;
  font-size: 14px;
}

</style>



// WEBPACK FOOTER //
// src/components/_modules/EvedPay/SubmitReceipt.vue